import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Eye, Pencil, Trash2, Clock, Users, CheckCircle, ExternalLink, Database, HelpCircle } from 'lucide-react';
import { listExamSets, deleteExamSet, publishExamSet } from '../lib/examApi';
import { getUser } from '../lib/auth';
import { useToast, useConfirm } from './ui';
import type { ExamSet } from '../types/exam';

const ExamManagement: React.FC = () => {
  const navigate = useNavigate();
  const user = getUser();
  const toast = useToast();
  const { confirm } = useConfirm();
  const [examSets, setExamSets] = useState<ExamSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadExamSets();
  }, []);

  const loadExamSets = async () => {
    try {
      setLoading(true);
      const sets = await listExamSets();
      setExamSets(sets);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    const confirmed = await confirm({
      title: 'Slett prøvesett',
      message: `Er du sikker på at du vil slette prøvesettet "${title}"? Dette kan ikke angres.`,
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      await deleteExamSet(id);
      await loadExamSets();
    } catch (err: any) {
      toast.showError('Feil ved sletting: ' + err.message);
    }
  };

  const handlePublish = async (id: string, title: string) => {
    const confirmed = await confirm({
      title: 'Publiser prøvesett',
      message: `Publiser prøvesettet "${title}"? Dette gjør det tilgjengelig for kandidater.`,
      confirmLabel: 'Publiser',
      cancelLabel: 'Avbryt',
      variant: 'default'
    });

    if (!confirmed) return;

    try {
      const publicLink = await publishExamSet(id);
      toast.showSuccess(`Prøven er publisert! Lenke: ${window.location.origin}/exam/${publicLink}`);
      await loadExamSets();
    } catch (err: any) {
      toast.showError('Feil ved publisering: ' + err.message);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      published: 'bg-green-100 text-green-800',
      archived: 'bg-red-100 text-red-800'
    };

    const labels = {
      draft: 'Utkast',
      published: 'Publisert',
      archived: 'Arkivert'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${styles[status as keyof typeof styles]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  const getGradingScaleLabel = (scale: string) => {
    const labels = {
      pass_fail: 'Bestått/Ikke bestått',
      three_tier: 'Meget godt bestått/Bestått/Ikke bestått',
      a_f: 'A-F',
      percentage: 'Prosent'
    };
    return labels[scale as keyof typeof labels] || scale;
  };

  const getQuestionCountDisplay = (exam: ExamSet) => {
    if (exam.use_question_bank) {
      return {
        count: exam.question_count || 20,
        label: 'fra bank',
        icon: Database,
        color: 'text-purple-600'
      };
    }
    return {
      count: exam.fixed_question_count || 0,
      label: 'faste',
      icon: HelpCircle,
      color: 'text-kf-blue-600'
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Laster prøver...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-medium">Feil ved lasting av prøver</h3>
        <p className="text-red-600 mt-1">{error}</p>
        <button
          onClick={loadExamSets}
          className="mt-2 text-sm text-red-700 underline"
        >
          Prøv igjen
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Prøvesystem</h1>
          <p className="text-gray-600 mt-1">Administrer prøver for studieplaner</p>
        </div>
        <button
          onClick={() => navigate('/exams/create')}
          className="flex items-center gap-2 px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700 transition"
        >
          <Plus size={20} />
          Opprett ny prøve
        </button>
      </div>

      {/* Stats */}
      {examSets.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Totalt prøver</div>
            <div className="text-2xl font-bold text-gray-900">{examSets.length}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Publiserte</div>
            <div className="text-2xl font-bold text-green-600">
              {examSets.filter(e => e.status === 'published').length}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Totale forsøk</div>
            <div className="text-2xl font-bold text-kf-blue-600">
              {examSets.reduce((sum, e) => sum + (e.total_attempts || 0), 0)}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Bestått</div>
            <div className="text-2xl font-bold text-green-600">
              {examSets.reduce((sum, e) => sum + (e.passed_count || 0), 0)}
            </div>
          </div>
        </div>
      )}

      {/* Exam List */}
      {examSets.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ingen prøver ennå</h3>
          <p className="text-gray-600 mb-4">Opprett din første prøve for en studieplan</p>
          <button
            onClick={() => navigate('/exams/create')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700"
          >
            <Plus size={20} />
            Opprett prøve
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prøve
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Spørsmål
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Innstillinger
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-help"
                  title="Personer-ikonet viser levert/påbegynt. Grønn hake viser antall som har bestått etter sensur — tall i parentes venter på sensur."
                >
                  Statistikk
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {examSets.map((exam) => (
                <tr key={exam.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-start">
                      <FileText className="text-kf-blue-600 mt-1 mr-3 flex-shrink-0" size={20} />
                      <div>
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                          {exam.title}
                          {/* CX: Kompositt-badge vises først, overstyrer single-type-badge */}
                          {(exam as any).composite_mode ? (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200"
                              title={`Kompositt-prøvesett: ${Array.isArray((exam as any).composite_template) ? (exam as any).composite_template.length : '?'} vurderingskomponenter — modus: ${(exam as any).composite_mode === 'weighted_composite' ? 'vektet sum' : 'alle må bestås'}`}
                            >
                              Kompositt ({Array.isArray((exam as any).composite_template) ? (exam as any).composite_template.length : '?'} komp.)
                            </span>
                          ) : exam.primary_assessment_type && exam.primary_assessment_type !== 'written' && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200"
                              title="Planlagt vurderingsform er ikke skriftlig SEB-prøve — sensor sensurerer manuelt."
                            >
                              {exam.primary_assessment_type === 'oral' && 'Muntlig'}
                              {exam.primary_assessment_type === 'project' && 'Prosjekt'}
                              {exam.primary_assessment_type === 'practical' && 'Praktisk'}
                              {exam.primary_assessment_type === 'portfolio' && 'Mappe'}
                              {exam.primary_assessment_type === 'observation' && 'Observasjon'}
                              {exam.primary_assessment_type === 'group' && 'Gruppe'}
                              {exam.primary_assessment_type === 'other' && 'Annen form'}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{exam.study_plan_title}</div>
                        {exam.description && (
                          <div className="text-xs text-gray-400 mt-1 line-clamp-2">{exam.description}</div>
                        )}
                        <div className="text-xs text-gray-400 mt-2">
                          Opprettet: {new Date(exam.created_at).toLocaleDateString('nb-NO')}
                          {exam.updated_at && exam.updated_at !== exam.created_at && (
                            <span className="ml-2">
                              | Sist endret: {new Date(exam.updated_at).toLocaleDateString('nb-NO')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(exam.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const qInfo = getQuestionCountDisplay(exam);
                      const IconComponent = qInfo.icon;
                      return (
                        <div className="flex items-center gap-1">
                          <IconComponent size={14} className={qInfo.color} />
                          <span className={`font-medium ${qInfo.color}`}>{qInfo.count}</span>
                          <span className="text-xs text-gray-500">{qInfo.label}</span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      <div className="flex items-center gap-1 mb-1">
                        <Clock size={14} className="text-gray-400" />
                        {exam.time_limit_minutes ? `${exam.time_limit_minutes} min` : 'Ingen grense'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {exam.pass_threshold}% for å bestå
                      </div>
                      <div className="text-xs text-gray-500">
                        {getGradingScaleLabel(exam.grading_scale)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {(() => {
                      const total = exam.total_attempts || 0;
                      const submitted = exam.submitted_count ?? 0;
                      const passed = exam.passed_count || 0;
                      const inProgress = Math.max(0, total - submitted);
                      const awaitingSensor = Math.max(0, submitted - passed);
                      const usersTitle = total === 0
                        ? 'Ingen kandidater har startet prøven ennå'
                        : `${submitted} av ${total} kandidater har levert${inProgress > 0 ? ` (${inProgress} har startet, men ikke levert)` : ''}`;
                      const passedTitle = total === 0
                        ? 'Ingen resultater ennå'
                        : `${passed} har bestått etter sensur${awaitingSensor > 0 ? ` — ${awaitingSensor} venter på sensur` : ''}`;
                      return (
                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1 cursor-help" title={usersTitle}>
                            <Users size={14} className="text-gray-400" />
                            <span className="text-gray-900">
                              {total === 0 ? '0' : `${submitted}/${total}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 cursor-help" title={passedTitle}>
                            <CheckCircle size={14} className={passed > 0 ? 'text-green-500' : 'text-gray-400'} />
                            <span className="text-gray-900">
                              {passed}
                              {awaitingSensor > 0 && (
                                <span className="text-amber-600 ml-1" title={`${awaitingSensor} venter på sensur`}>
                                  (+{awaitingSensor})
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {exam.status === 'published' && (
                        <button
                          onClick={() => window.open(`/exam/${exam.public_link}`, '_blank')}
                          className="text-kf-blue-600 hover:text-kf-blue-900"
                          title="Åpne prøvelenke"
                        >
                          <ExternalLink size={18} />
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/exams/${exam.id}`)}
                        className="text-kf-blue-600 hover:text-kf-blue-900"
                        title="Vis detaljer"
                      >
                        <Eye size={18} />
                      </button>
                      {/* Sensor role: read-only access, no edit/publish/delete buttons */}
                      {user?.role !== 'sensor' && (
                        <>
                          <button
                            onClick={() => navigate(`/exams/edit/${exam.id}`)}
                            className="text-kf-blue-600 hover:text-kf-blue-900"
                            title="Rediger"
                          >
                            <Pencil size={18} />
                          </button>
                          {exam.status === 'draft' && (
                            <button
                              onClick={() => handlePublish(exam.id, exam.title)}
                              className="text-green-600 hover:text-green-900"
                              title="Publiser"
                            >
                              <CheckCircle size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(exam.id, exam.title)}
                            className="text-red-600 hover:text-red-900"
                            title="Slett"
                          >
                            <Trash2 size={18} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ExamManagement;
